/**
 * CaseFileModal — the case file popped out over everything.
 *
 * Scott, 2026-09-08: "Looks like it needs to be maximizable" … and then, on
 * the first cut which grew the case file in place: "The expanse should be a
 * full pop-out modal."
 *
 * So maximising is not a bigger panel or a taller box any more — it is this
 * one modal, opened from the ⤢ on whichever surface Jess is on (the Cases-tab
 * investigations panel, or the case file embedded in the triage board). Both
 * hosts render the same component, so the big view is identical either way.
 *
 * Deliberately transient: unlike a width, "the modal is open" is not a
 * preference worth remembering — a case file that pops itself open every time
 * the tab loads would be an ambush. It opens on the ⤢ and closes on ✕, the
 * backdrop, or Escape.
 */
import { useEffect } from 'react';

import { InvestigationCaseFile } from './InvestigationCaseFile';
import { STATUS_PILL_CLASS } from './caseFileView';
import { STATUS_LABEL } from './investigationsView';
import type { DoorDataRequestRead } from '../../services/qcpDataRequests';
import type { DoorCaseTaskRead, DoorInvestigationRead } from '../../services/qcpInvestigations';
import styles from './CaseFileModal.module.css';

export interface CaseFileModalProps {
  investigation: DoorInvestigationRead;
  onClose: () => void;
  onUpdated: (updated: DoorInvestigationRead) => void;
  /** Jump to the conversation behind this case, when a thread links here. */
  onOpenConversation?: () => void;
  /** Fallback for cases saved straight from chat (no thread). */
  onContinueInChat?: () => void;
  /** The conversation the host shows beside the case (see InvestigationCaseFile). */
  activeConversationId?: string | null;
  /** "Open" on a linked-conversation row (see InvestigationCaseFile). */
  onOpenLinkedConversation?: (conversationId: string) => void;
  /** The case's asks and "Ask <desk>" on an outstanding item (see InvestigationCaseFile). */
  asks?: ReadonlyArray<DoorDataRequestRead>;
  onAskTask?: (task: DoorCaseTaskRead) => void;
  taskDesks?: Record<string, string>;
}

export function CaseFileModal({
  investigation,
  onClose,
  onUpdated,
  onOpenConversation,
  onContinueInChat,
  activeConversationId,
  onOpenLinkedConversation,
  asks,
  onAskTask,
  taskDesks,
}: CaseFileModalProps) {
  // Escape closes, the way every other overlay on the door behaves.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <>
      <div
        className={styles.backdrop}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label={`Case file ${investigation.investigation_no}`}
      >
        {/* Pinned: the case's identity and the way out stay put while the
            case file itself scrolls. */}
        <header className={styles.head}>
          <span className={styles.headNo}>
            {investigation.investigation_no}
          </span>
          <span
            className={`${styles.statusPill} ${STATUS_PILL_CLASS[investigation.status]}`}
          >
            {STATUS_LABEL[investigation.status]}
          </span>
          <span className={styles.headTitle}>{investigation.title}</span>
          <button
            type="button"
            className={styles.shrinkBtn}
            onClick={onClose}
            aria-label="Shrink case file"
            title="Back to the page"
          >
            <span aria-hidden="true">⤡</span>
          </button>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close case file"
          >
            ✕
          </button>
        </header>

        <div className={styles.body}>
          <InvestigationCaseFile
            key={investigation.id}
            investigation={investigation}
            onUpdated={onUpdated}
            embedded
            popped
            onOpenConversation={onOpenConversation}
            onContinueInChat={onContinueInChat}
            activeConversationId={activeConversationId}
            onOpenLinkedConversation={onOpenLinkedConversation}
            asks={asks}
            onAskTask={onAskTask}
            taskDesks={taskDesks}
          />
        </div>
      </div>
    </>
  );
}

export default CaseFileModal;
